import { properties } from "@repoflow/common/schema-fields";

const { createdAt, updatedAt } = properties;

export { createdAt, updatedAt };

export const orgId = {
  type: "string",
};

// Chosen by the caller, not minted — it is what a capture will be posted
// against and what the list reads as ("onboarding", "support-intake"), and a
// uuid there would make both opaque. That is why the model opts into
// `createIfNotExists`: with a caller-chosen key a plain put silently replaces
// somebody else's workflow.
//
// /Name/Required is lowercase-kebab and non-empty. It comes from
// @repoflow/schema/utils, which every schema in this tree spreads.
export const workflowId = {
  label: "Name",
  $ref: "/Name/Required",
  minLength: 1,
  default: "",
};

export const title = {
  label: "Title",
  type: "string",
  minLength: 1,
};

export const description = {
  label: "Description",
  type: "string",
  textarea: true,
  default: "",
};

// The capture steps, in the order they are asked. Each is a /Workflow/Step —
// a name, a label and the params that declare its fields — and the array is
// embedded in the row rather than kept as a child table: a workflow is edited
// whole, its steps have no life of their own, and there are never more of them
// than a form can show.
//
// Two forms of the same field. `steps` is what a form renders and what the API
// validates; `storedSteps` is what the table can validate. The difference is
// the `resolver` union inside `/Types/Param.config`: raw jsonschema has never
// heard of `resolver`, evaluates the `oneOf` literally, finds that several
// branches match (Color and Icon are structurally identical — one optional
// string) and rejects every real value with "is not exactly one from". datorm
// validates writes with raw jsonschema, so a storage shape carrying the union
// would refuse every workflow with a field on it. The API boundary does the
// resolver-aware pass (api/code/src/validate) and GraphQL carries the array as
// opaque JSON, so the check lives where it works. Same split, same reason, as
// pagews' `types`/`storedTypes`.
export const steps = {
  label: "Steps",
  type: "array",
  items: { $ref: "/Workflow/Step" },
  index: "name",
  default: [],
};

export const storedSteps = {
  type: "array",
  items: { type: "object" },
  default: [],
};

// The workflow's subforms: named field groups an `object` param on any step
// names, held once for the whole workflow rather than inside the step that
// first needed one. Same split as `steps`, for the same reason — every
// subform's params carry the resolver union.
export const subforms = {
  label: "Subforms",
  type: "array",
  items: { $ref: "/Workflow/Subform" },
  index: "name",
  default: [],
};

export const storedSubforms = {
  type: "array",
  items: { type: "object" },
  default: [],
};

export const tags = {
  label: "Tags",
  type: "array",
  items: { type: "string" },
  widget: "tags",
  default: [],
};

export const status = {
  type: "string",
};

// Which published version the share link serves: 0 until the first publish,
// then the number of the newest row in workflow-versions-table. The draft on
// this row moves with every save; the version rows never do, and this is the
// pointer from the one to the other. The model fills it in on every write, so
// a row from before publishing existed reads as never published.
export const publishedVersion = {
  label: "Published version",
  type: "integer",
  minimum: 0,
  default: 0,
};

// The share link's token, on the PUBLIC shape only.
//
// Deliberately absent from /Data/Workflow. The token is what an anonymous
// visitor's link carries, so it is resolved through a GSI on the table
// (`shareToken-index`), and DynamoDB refuses an empty string or a null as a
// GSI key attribute — while datorm serializes every schema property on every
// write, with absent as `null`. A schema property here would therefore make
// every save of an unshared workflow fail. So the attribute lives BESIDE the
// schema: written and removed by data/workflow.share with its own update
// expression, read back by a custom entity, and never touched by an ordinary
// save. `""` on the wire means "not shared"; a row that is not shared has no
// attribute at all, which is what keeps the index sparse.
export const shareToken = {
  type: "string",
  default: "",
};
